// src/pages/About.js

import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';

import loKtrLogo from '../assets/loKtrLogo.png';
import TopBar from '../components/TopBar/TopBar';
import Background from '../components/Background/Background';
import ContactUsDialog from '../components/ContactUsDialog/ContactUsDialog';

import packageJson from '../../package.json';

const About = () => {
  // === LAYOUT POSITIONING CONTROLS ===
  const gridTopPosition = '70px';
  const bodyFontSize = 'clamp(13px, min(1.5vw, 2.5vh), 28px)';

  const [dialogOpen, setDialogOpen] = useState(false);

  const handleOpenDialog = () => setDialogOpen(true);
  const handleCloseDialog = () => setDialogOpen(false);

  return (
    <Background>
      {/* === INJECT TOP MENU BAR === */}
      <TopBar activeButton='about' />

      {/* === VERTICAL FLEX LAYOUT === */}
      <Box
        sx={{
          position: 'absolute',
          top: gridTopPosition,
          bottom: '20px',
          left: '20px',
          right: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
          zIndex: 1,
        }}>
        {/* ROW 1: Logo (flex: 1) */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            p: 2,
            minHeight: 0,
            overflow: 'hidden',
          }}>
          <img
            src={loKtrLogo}
            alt='loKtr Logo'
            style={{
              maxWidth: '100%',
              maxHeight: '41.5%',
              objectFit: 'contain',
              marginTop: '-71px',
            }}
          />
        </Box>

        {/* ROW 2: The 4-Column Grid for Text (flex: 1.5) */}
        <Box
          sx={{
            flex: 1.5,
            display: 'grid',
            gridTemplateColumns: '1fr 2fr 2fr 1fr', // Column ratio: x, 2x, 2x, x
            gap: '24px',
            mt: '-80px',
            minHeight: 0,
          }}>
          {/* Column 1: Blank (x wide) */}
          <Box />

          {/* Column 2: The Features & Audience (2x wide) */}
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              p: { xs: 1.5, md: 3 },
              color: '#000000',
              minHeight: 0,
            }}>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: bodyFontSize,
                fontWeight: 700,
                lineHeight: 1.2,
                mb: 1,
              }}>
              <span style={{ color: 'royalblue', fontWeight: 700 }}>loKtr</span> is built
              for:
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: bodyFontSize,
                fontWeight: 500,
                lineHeight: 1.2,
                mb: 0.5,
                display: 'flex',
                alignItems: 'center',
              }}>
              <span
                style={{
                  display: 'inline-block',
                  fontSize: '1.2em',
                  marginRight: '12px',
                  transform: 'translateY(-2px)',
                }}>
                •
              </span>
              Service providers
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: bodyFontSize,
                fontWeight: 500,
                lineHeight: 1.2,
                mb: 0.5,
                display: 'flex',
                alignItems: 'center',
              }}>
              <span
                style={{
                  display: 'inline-block',
                  fontSize: '1.2em',
                  marginRight: '12px',
                  transform: 'translateY(-2px)',
                }}>
                •
              </span>
              Product suppliers
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: bodyFontSize,
                fontWeight: 500,
                lineHeight: 1.2,
                mt: 1,
              }}>
              Designed for businesses delivering across multiple locations
            </Typography>

            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: bodyFontSize,
                fontWeight: 700,
                lineHeight: 1.2,
                mb: 1,
                mt: 4,
              }}>
              The <span style={{ color: 'royalblue', fontWeight: 700 }}>loKtr</span>{' '}
              platform provides:
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: bodyFontSize,
                fontWeight: 500,
                lineHeight: 1.2,
                mb: 0.5,
                display: 'flex',
                alignItems: 'center',
              }}>
              <span
                style={{
                  display: 'inline-block',
                  fontSize: '1.2em',
                  marginRight: '12px',
                  transform: 'translateY(-2px)',
                }}>
                •
              </span>
              real-time experience documents
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: bodyFontSize,
                fontWeight: 500,
                lineHeight: 1.2,
                mb: 0.5,
                display: 'flex',
                alignItems: 'center',
              }}>
              <span
                style={{
                  display: 'inline-block',
                  fontSize: '1.2em',
                  marginRight: '12px',
                  transform: 'translateY(-2px)',
                }}>
                •
              </span>
              live company newsfeeds
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: bodyFontSize,
                fontWeight: 500,
                lineHeight: 1.2,
                mb: 0.5,
                display: 'flex',
                alignItems: 'center',
              }}>
              <span
                style={{
                  display: 'inline-block',
                  fontSize: '1.2em',
                  marginRight: '12px',
                  marginTop: '-24px',
                  transform: 'translateY(-2px)',
                }}>
                •
              </span>
              a comprehensive self-managed mail broadcast platform
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: bodyFontSize,
                fontWeight: 500,
                lineHeight: 1.2,
                display: 'flex',
                alignItems: 'center',
              }}>
              <span
                style={{
                  display: 'inline-block',
                  fontSize: '1.2em',
                  marginRight: '12px',
                  transform: 'translateY(-2px)',
                }}>
                •
              </span>
              a dedicated client app
            </Typography>
          </Box>

          {/* Column 3: The Benefits (2x wide) */}
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              p: { xs: 1.5, md: 3 },
              color: '#000000',
              minHeight: 0,
            }}>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: bodyFontSize,
                fontWeight: 700,
                lineHeight: 1.2,
                mb: 1,
              }}>
              Your track record is your strongest asset
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: bodyFontSize,
                fontWeight: 500,
                lineHeight: 1.2,
                mb: 1,
                display: 'flex',
                alignItems: 'flex-start',
              }}>
              <span
                style={{
                  display: 'inline-block',
                  fontSize: '1.2em',
                  marginRight: '12px',
                  marginTop: '-4px',
                }}>
                •
              </span>
              <span>
                It wins bids: Proven experience secures new projects and contracts
              </span>
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: bodyFontSize,
                fontWeight: 500,
                lineHeight: 1.2,
                mb: 1,
                display: 'flex',
                alignItems: 'flex-start',
              }}>
              <span
                style={{
                  display: 'inline-block',
                  fontSize: '1.2em',
                  marginRight: '12px',
                  marginTop: '-4px',
                }}>
                •
              </span>
              <span>
                It requires a strategy: Enhancing how you display your work is the
                smartest investment you can make
              </span>
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: bodyFontSize,
                fontWeight: 500,
                lineHeight: 1.2,
                mb: 1,
                display: 'flex',
                alignItems: 'flex-start',
              }}>
              <span
                style={{
                  display: 'inline-block',
                  fontSize: '1.2em',
                  marginRight: '12px',
                  marginTop: '-4px',
                }}>
                •
              </span>
              <span>
                <span style={{ color: 'royalblue', fontWeight: 500 }}>loKtr</span> is that
                strategy: We complement your existing website with a dedicated tool to
                systematically manage and share your company's history
              </span>
            </Typography>
          </Box>

          {/* Column 4: Blank */}
          <Box />
        </Box>

        {/* ROW 3: Invisible Spacer */}
        <Box sx={{ flex: 0.75, minHeight: 0 }} />
      </Box>

      {/* ========================================================================= */}
      {/* === UNIVERSAL FOOTER === */}
      {/* ========================================================================= */}
      <Box
        sx={{
          position: 'absolute',
          bottom: '20px', // Anchors to the bottom of the screen
          left: 0,
          right: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          backgroundColor: 'transparent',
          zIndex: 10,
        }}>
        <Typography
          onClick={handleOpenDialog} // Triggers the contact dialog
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontSize: 'clamp(14px, 1.5vw, 18px)',
            fontWeight: 400,
            color: '#000000',
            opacity: 0.9,
            cursor: 'pointer',
            transition: 'opacity 0.2s ease',
            '&:hover': { textDecoration: 'underline', opacity: 1 },
          }}>
          info@loktr.net
        </Typography>
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontSize: 'clamp(11px, 1vw, 14px)',
              color: '#000000',
              lineHeight: 1.3,
              opacity: 0.8,
            }}>
            Version: {packageJson.version}
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontSize: 'clamp(11px, 1vw, 14px)',
              color: '#000000',
              lineHeight: 1.3,
              opacity: 0.8,
            }}>
            (c) RTA Technology Pty Ltd. All rights reserved.
          </Typography>
        </Box>
      </Box>

      {/* === EXTRACTED CONTACT FORM POPUP === */}
      <ContactUsDialog open={dialogOpen} onClose={handleCloseDialog} />
    </Background>
  );
};

export default About;
